import { useState, type KeyboardEvent } from 'react';
import Card from '../../components/card/Card';
import Input from '../../components/input/Input';
import Button from '../../components/button/Button';
import Spinner from '../../components/Spinner/spinner';
import P from '../../components/texts/p/P';
import Image from '../../components/image/Image';
import { searchMonetizationRequestByCuit } from '../../services/GuaranteesService';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import type { MonetizationRequestSearchResponse } from './types';

interface CuitSearchStepProps {
  cuit: string;
  onCuitChange: (cuit: string) => void;
  onSearchSuccess: (result: MonetizationRequestSearchResponse) => void;
  onError: (error: string) => void;
}

const normalizeToDigitsOnly = (value: string): string => value.replace(/-/g, '');

const CuitSearchStep = ({ 
  cuit, 
  onCuitChange, 
  onSearchSuccess, 
  onError 
}: CuitSearchStepProps) => {
  const fiscalId = useFiscalIdConfig();
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validateCuit = (input: string): { valid: boolean; normalized: string; error: string | null } => {
    const trimmed = input.trim();
    
    if (!trimmed) {
      return { valid: false, normalized: '', error: `Ingresá un ${fiscalId.label} para buscar.` };
    }

    const normalized = normalizeToDigitsOnly(trimmed);
    
    if (!fiscalId.isValidDigits(normalized)) {
      return { 
        valid: false, 
        normalized, 
        error: fiscalId.invalidLengthMessage 
      };
    }

    return { valid: true, normalized, error: null };
  };

  const handleSearch = async () => {
    const validation = validateCuit(cuit);
    
    if (!validation.valid) {
      setErrorMessage(validation.error);
      onError(validation.error || 'Error de validación');
      return;
    }

    setIsSearching(true);
    setErrorMessage(null);
    setHasSearched(true);

    try {
      const result = await searchMonetizationRequestByCuit(validation.normalized);
      
      if (result.message === 'CUIT_FORMAT_INVALID') {
        setErrorMessage(`El formato del ${fiscalId.label} es inválido.`);
        onError(`El formato del ${fiscalId.label} es inválido.`);
      } else if (result.message === 'CUIT_NOT_FOUND') {
        setErrorMessage(`El ${fiscalId.label} ${fiscalId.format(validation.normalized)} no tiene carpetas cerradas ni productos habilitados.`);
        onError(`${fiscalId.label} no encontrado`);
      } else if (result.message === 'NO_ENABLED_PRODUCTS') {
        setErrorMessage(`El cliente ${result.companyName || fiscalId.label + ' ' + fiscalId.format(validation.normalized)} no tiene productos habilitados para monetizar.`);
        onError('Sin productos habilitados');
      } else if (result.products && result.products.length > 0) {
        onSearchSuccess(result);
      } else {
        setErrorMessage(`No se encontraron productos habilitados para este ${fiscalId.label}.`);
        onError('Sin productos habilitados');
      }
    } catch (error: any) {
      const statusCode = error?.response?.status;
      
      if (statusCode === 400) {
        setErrorMessage(`El formato del ${fiscalId.label} es inválido.`);
      } else if (statusCode === 404) {
        setErrorMessage(`El ${fiscalId.label} ${fiscalId.format(validation.normalized)} no está registrado o no tiene carpetas cerradas.`);
      } else {
        setErrorMessage(`Error al buscar el ${fiscalId.label}. Por favor, intentá nuevamente.`);
      }
      
      onError(errorMessage || 'Error de búsqueda');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      void handleSearch();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onCuitChange(value);
    
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <Card width='100%'>
      <Card margin={{ bottom: '4' }}>
        <P variant='neutral' modifier='strong' weight='bold'>
          Ingresá el {fiscalId.label} del cliente para ver sus productos habilitados
        </P>
      </Card>

      <Card width='400px' display='flex' gap='16px' margin={{ bottom: '3' }}>
        <Input
          type='text'
          size='md'
          icons={{ iconLeft: 'search_outline' }}
          placeholder={fiscalId.enterPlaceholder}
          value={cuit}
          onChange={handleInputChange}
          onKeyup={handleKeyUp}
          error={!!errorMessage}
        />
        <Button 
          variant='tertiary' 
          size='sm' 
          onClick={() => void handleSearch()}
          disabled={isSearching}
        >
          {isSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
        </Button>
      </Card>

      {errorMessage && (
        <Card 
          variant='danger' 
          modifier='inverted' 
          spacing='3' 
          margin={{ top: '2' }}
          borderRadiusAmount='2'
        >
          <P variant='danger' modifier='strong' withMargin={false}>
            {errorMessage}
          </P>
        </Card>
      )}

      {hasSearched && !isSearching && !errorMessage && (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '8', bottom: '8' }}
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      )}
    </Card>
  );
};

export default CuitSearchStep;
